import React, { useMemo, useState } from 'react';

import Plus from '@ast/magma/components/icon/icons/Plus';

import { Menu } from '@ast/magma/components/menu';

import { FieldInfoGroupField, SelectableGroupField } from '@app/common/configurable-wizards';
import { Field, FieldComponent, FieldLocator } from '@app/common/configurable-wizards/types';
import { combineNames } from '@app/common/configurable-wizards/utils';

import { ActionListItem } from '@app/widgets/contacts-and-settings/components/ActionListItem';
import { useSettingFieldsContext } from '@app/widgets/contacts-and-settings/contexts/SettingFieldsContext';
import { useUserSettingsTranslation } from '@app/widgets/contacts-and-settings/hooks/useUserSettingsTranslation';

import { SettingFieldsProvider } from '../../contexts/AddressFormContext';
import { EmptyFields } from '../../types';
import { getSelectedAddressField, isPhysicalAddressField } from '../../utils/address';
import { canAddMailingAddress } from '../../utils/addressPermissions';

import { AddressEditForm } from './AddressEditForm';
import styles from './MemberAddressFieldsList.pcss';

const sortFields = (a: SelectableGroupField, b: SelectableGroupField) => {
  if (isPhysicalAddressField(a) && !isPhysicalAddressField(b)) {
    return -1;
  }
  if (!isPhysicalAddressField(a) && isPhysicalAddressField(b)) {
    return 1;
  }
  return 0;
};

export const MemberAddressFieldsList: FieldComponent<FieldInfoGroupField> = ({ parent, field, locator }) => {
  const { t } = useUserSettingsTranslation();
  const { permissions, emptyFields } = useSettingFieldsContext();
  const epmtyAddressFields = emptyFields?.[EmptyFields.ADDRESS] as SelectableGroupField;
  const canAdd = useMemo(
    () => canAddMailingAddress(field, permissions),
    [field, permissions],
  );

  const emptyField = getSelectedAddressField(epmtyAddressFields);
  const [isOpen, setIsOpen] = useState(false);

  const memberAccountNumber = field.id.replace(/^\D+/g, '');

  return (
    <div>
      {(field.subFields as SelectableGroupField[])
        .sort(sortFields)
        .map((item, index) => {
          const subField = item as Field;
          const Component = locator(subField);

          return (
            <Component
              // eslint-disable-next-line react/no-array-index-key
              key={`${subField.id}-${index}`}
              parent={combineNames(parent, field.id)}
              field={subField}
              locator={locator}
            />
          );
        })}
      {emptyField && canAdd && (
        <Menu>
          <ActionListItem
            data-stable-name="AddMailingAddress"
            data={t(
              'mailing-address-fields-list.submit-address.action-button|Submit new mailing address action button',
              'Add separate mailing address',
            )}
            getItemIcon={() => <Plus className={styles.addItemIcon} />}
            onClick={() => setIsOpen(true)}
          />
        </Menu>
      )}
      {isOpen && (
        <SettingFieldsProvider>
          <AddressEditForm
            field={epmtyAddressFields}
            onClose={() => setIsOpen(false)}
            valuesModifier={{
              IsPrimary: 'false',
              ...(memberAccountNumber ? { MemberAccountNumber: memberAccountNumber } : {}),
            }}
            successMessage={t(
              'mailing-address-fields-list.alert.success|Message text for succesful submit new mailing address',
              'Your mailing address has been successfully added',
            )}
            dialogOptions={{
              title: t(
                'mailing-address-fields-list.submit-dialog.title|Submit new mailing address dialog title',
                'Add separate mailing address',
              ),
              showClose: {
                // eslint-disable-next-line i18next/no-literal-string
                'aria-label': t(
                  // eslint-disable-next-line max-len
                  'address-fields-list.submit-dialog.close-button.aria-label|Accessibility text for close button of submit mailing address dialog',
                  'Close new mailing address dialog',
                ),
              },
            }}
          />
        </SettingFieldsProvider>
      )}
    </div>
  );
};

export const MemberAddressFieldsListLocator: FieldLocator = ({ field }) => (
  field.id.startsWith('MemberAccountGroup')
    ? MemberAddressFieldsList
    : undefined
);
